import React, { useMemo, useState } from 'react';
import { DownloadSimple } from 'phosphor-react';
import CryptoJS from 'crypto-js';
import { COLORS } from '../../../../utils/colors';
import { DillButton, DillModal } from '../../../general/Dill';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { CreditApplicationFieldsValues } from '@dill/dill-shared/dist/src';
import {
  downloadFile,
  firebaseLargeFileUploadHelper,
  openDillToast,
} from '../../../../utils/helpers';
import { updateSupplierCreditApplication } from '../../../../redux/services/creditApplicationsService';
import { UploadFileInputs } from './UploadFileInput';

const UpdateFileModal = ({
  open,
  handleClose,
  allStepFieldsValues,
  selectedField,
  creditApplicationId,
}: {
  open: boolean;
  handleClose: () => void;
  allStepFieldsValues: CreditApplicationFieldsValues[];
  selectedField: CreditApplicationFieldsValues | null;
  creditApplicationId: string;
}) => {
  const dispatch = useAppDispatch();
  const [updatedFieldsValues, setUpdatedFieldsValues] = useState<
    { id: string; name: string; isRequired: boolean; value: any }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<
    {
      uniqueId: string;
      errorText: string;
    }[]
  >([]);

  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const handleCloseModal = () => {
    setUpdatedFieldsValues([]);
    setIsLoading(false);
    setErrors([]);
    handleClose();
  };

  const validateForm = () => {
    const fieldErrors: { uniqueId: string; errorText: string }[] = [];

    for (const field of updatedFieldsValues) {
      // Required field check
      let value = field.value;
      if (typeof value === 'string') {
        value = value.trim();
      }
      if (field.isRequired && !value) {
        fieldErrors.push({
          uniqueId: field.id,
          errorText: `${field.name} is required.`,
        });
      }
    }

    setErrors(fieldErrors);
    return fieldErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    const fields = await Promise.all(
      updatedFieldsValues.map(async (field) => {
        let value = field.value;
        if (typeof field.value === 'object' && field.value instanceof File) {
          const fileType = value.name.split('.').pop() || '';
          const uploadFileUrl = await firebaseLargeFileUploadHelper({
            blobUrl: URL.createObjectURL(value),
            fileNameWithExtension: value.name,
            storagePath: `creditApplicationFiles/${currentSupplier?.id || ''}/${Date.now()}`,
          });
          value = {
            name: value.name,
            url: uploadFileUrl ?? '',
            fileType: fileType,
          };
        }
        return { ...field, value };
      })
    );
    // allStepFieldsValues is the decrypted data shown on the page and the whole application is
    // saved back, so every hashed value has to be re-encrypted, including the ones nested under
    // conditional fields the upload didn't touch (e.g. an SSN under Organization Type).
    const secretKey = process.env.REACT_APP_CRYPTO_KEY;
    const withUpdatedValue = <T extends { uniqueId: string; isHashed?: boolean; value?: any }>(
      field: T
    ): T => {
      const updatedField = fields.find((f) => f.id === field.uniqueId);
      const next = updatedField ? { ...field, value: updatedField.value } : field;
      const isEncrypted = typeof next.value === 'string' && next.value.startsWith('U2FsdGVkX1');
      if (!next.isHashed || !next.value || isEncrypted || !secretKey) return next;
      return {
        ...next,
        value: CryptoJS.AES.encrypt(JSON.stringify(next.value), secretKey).toString(),
      };
    };
    const encryptedFields = allStepFieldsValues.map((field) => ({
      ...withUpdatedValue(field),
      ...(field.fieldValue ? { fieldValue: field.fieldValue.map(withUpdatedValue) } : {}),
    }));
    const response = await dispatch(
      updateSupplierCreditApplication({
        creditApplicationId: creditApplicationId,
        supplierId: currentSupplier?.id || '',
        fieldValues: encryptedFields,
        savingType: 'update signature',
        preserveSignatures: true,
      })
    );

    if (response.type === 'creditApplications/updateSupplierCreditApplication/fulfilled') {
      openDillToast({
        message: 'Files updated successfully',
        type: 'SUCCESS',
      });
      handleCloseModal();
    } else {
      openDillToast({
        message: 'Failed to update files',
        type: 'ERROR',
      });
    }
    setIsLoading(false);
  };

  const onChangeFormData = ({ id, value }: { id: string; value: any }) => {
    setUpdatedFieldsValues((prev) => prev.map((f) => (f.id === id ? { ...f, value: value } : f)));
  };

  const handleDownloadFile = async (fileUrl: string, fileName: string) => {
    downloadFile(fileUrl ?? '', fileName);
  };

  const onlyFileTypes = useMemo(() => {
    if (!selectedField) return [];
    const fields = [selectedField];
    setUpdatedFieldsValues(
      fields.map((field) => ({
        id: field.uniqueId,
        name: field.name,
        isRequired: field.isRequired,
        value: null,
      }))
    );
    return fields;
  }, [selectedField, allStepFieldsValues]);

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Update File"
      maxWidth="600px"
      footer={
        <div className="flex justify-end w-full gap-2">
          <DillButton
            label="Close"
            onClick={handleCloseModal}
            variant="secondary"
            isDisabled={isLoading}
          />
          <DillButton
            label="Submit"
            onClick={handleSubmit}
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      }>
      <div className="flex flex-col">
        {onlyFileTypes.length > 0 && (
          <div>
            <div className="flex gap-4 w-full border-b pb-1 mb-1">
              <h3 className="text-base font-semibold mb-2 w-full">{`Current File${
                onlyFileTypes.length > 1 ? 's' : ''
              }`}</h3>
              <h3 className="text-base font-semibold mb-2 w-full">{`Upload File${
                onlyFileTypes.length > 1 ? 's' : ''
              }`}</h3>
            </div>
            <div className="flex flex-col gap-3">
              {onlyFileTypes.map((field) => {
                const getValue = updatedFieldsValues.find((f) => f.id === field.uniqueId)?.value;
                return (
                  <div
                    key={field.id}
                    className="flex gap-4 w-full border-b pb-3 mb-1 last:border-b-0 last:pb-0 last:mb-0">
                    <div className="min-w-0 w-full">
                      <div className="mb-1 w-full text-sm font-medium break-words">
                        {field.fromField === 'CONDITIONAL_OPTION'
                          ? field.secondaryText || field.name
                          : field.name}
                      </div>
                      {field.fromField === 'CONDITIONAL_OPTION' && (
                        <div className="mb-1 text-xs text-gray-500">{field.name}: Yes</div>
                      )}
                      {field.value?.url ? (
                        <div className="flex gap-2 items-center">
                          <DillButton
                            variant="icon-default"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDownloadFile(field.value?.url ?? '', field.value?.name ?? '');
                            }}
                            icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                            width={30}
                            height={30}
                          />
                          <div className="text-sm text-gray-600 break-all min-w-0">
                            {field.value?.name || ''}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">No file uploaded</div>
                      )}
                    </div>
                    <div className="min-w-0 w-full">
                      <UploadFileInputs
                        isRequired={field.isRequired}
                        errorText={
                          errors.find((e) => e.uniqueId === field.uniqueId)?.errorText || ''
                        }
                        label={field.secondaryText || field.name}
                        selectedFile={
                          typeof getValue === 'object' && getValue instanceof File ? getValue : null
                        }
                        onSelectFile={(data) => {
                          onChangeFormData({
                            id: field.uniqueId,
                            value: data.file,
                          });
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DillModal>
  );
};

export default UpdateFileModal;
